/**
 * PATH: packages/ui/src/kanban/components/settings-home/useSettingsViewport.ts
 * FILE_ID: UI-KANBAN-SETTINGS-VIEWPORT-001
 * PURPOSE: Classify settings shell width: desktop 1024+, tablet 768-1023, phone <768.
 * ROLE IN ARCHITECTURE: Presentational breakpoint hook for SettingsHomeShell.
 * MAIN EXPORTS: SettingsViewport, useSettingsViewport.
 * NON-RESPONSIBILITIES: Does not persist preview size or mutate prefs.
 * NOTES FOR FUTURE AI: Missing matchMedia defaults to desktop so tests keep the rail.
 */

import { useEffect, useState } from 'react'

export type SettingsViewport = 'phone' | 'tablet' | 'desktop'

function readViewport(): SettingsViewport {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop'
  }
  try {
    if (window.matchMedia('(max-width: 767px)').matches) return 'phone'
    if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet'
    return 'desktop'
  } catch {
    return 'desktop'
  }
}

/** Subscribe to the settings responsive breakpoints. */
export function useSettingsViewport(): SettingsViewport {
  const [viewport, setViewport] = useState<SettingsViewport>(readViewport)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const phone = window.matchMedia('(max-width: 767px)')
    const tablet = window.matchMedia('(max-width: 1023px)')
    const update = () => setViewport(readViewport())
    phone.addEventListener('change', update)
    tablet.addEventListener('change', update)
    update()
    return () => {
      phone.removeEventListener('change', update)
      tablet.removeEventListener('change', update)
    }
  }, [])
  return viewport
}
